import React from 'react';
import { MessageCircle, Calendar, CheckCircle, Shield, Clock, Tag, Home, Building2, Archive } from 'lucide-react';
import ServicePageLayout from '../components/ServicePageLayout';
import { P } from '../constants';

const data = {
  seo: {
    title: 'Haushaltsauflösung Wien » Fixpreis ✓ Wertanrechnung – Wohnungsräumung Wien',
    description: 'Haushaltsauflösung Wien professionell & zum Fixpreis ✓ Wertanrechnung für Möbel ✓ Besenreine Übergabe ✓ Kostenlose Besichtigung. Jetzt anfragen!',
    keywords: 'Haushaltsauflösung Wien, Haushaltsauflösung Wien Kosten, Haushalt auflösen Wien, Haushaltsauflösung Fixpreis Wien',
    canonical: '/haushaltsaufloesung-wien',
    h1short: 'Haushaltsauflösung Wien',
  },
  schemas: [{
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Haushaltsauflösung Wien',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': 'Wien',
    'description': 'Vollständige Haushaltsauflösung in Wien mit Wertanrechnung, Fixpreisgarantie und besenreiner Übergabe.',
    'offers': { '@type': 'Offer', 'price': '490', 'priceCurrency': 'EUR' },
  }],
  hero: {
    badge: 'Wertanrechnung inklusive',
    headline: <><span>Haushaltsauflösung<br />Wien</span><br /><span className="text-[#E8722A]">mit Wertanrechnung.</span></>,
    subline: 'Ein ganzer Haushalt. Unser Job.',
    bullets: [
      'Jahrzehnte Möbel und Hausrat – wir kümmern uns',
      'Wertanrechnung: Gute Stücke senken Ihren Preis direkt',
      'Kostenlose Besichtigung & verbindlicher Fixpreis',
      'Besenreine Übergabe. Immer. Ohne Aufpreis.',
      'Familiengeführter Betrieb – Sie reden mit dem Chef',
    ],
    imageSrc: P.movingBoxes,
    imageAlt: 'Haushaltsauflösung Wien – Umzugskartons',
  },
  formTitle: 'Haushaltsauflösung anfragen –',
  serviceName: 'Haushaltsauflösung',
  serviceCard: {
    heading: 'Haushaltsauflösung Wien – Sie müssen nicht einen Finger rühren',
    paragraphs: [
      'Jahrzehnte Möbel, Geschirr, Kram – und jetzt sollen Sie das alleine durchforsten? Müssen Sie nicht.',
      'Wir übernehmen den ganzen Haushalt. Was noch brauchbar ist, zieht den Preis runter. Was weg muss, kommt weg. Besenrein übergeben. Fertig.',
    ],
    listItems: [
      'Vollständige Auflösung – Möbel, Hausrat, Elektrogeräte, Kleidung, alles',
      'Wertanrechnung: Gute Stücke senken Ihren Preis direkt',
      'Sortenreine Entsorgung nach österreichischen Richtlinien',
      'Sie müssen nichts vorbereiten – wir übernehmen von Anfang bis Ende',
      'Besenreine Übergabe – bereit für den nächsten Schritt',
    ],
    imageSrc: P.furniturePile,
    imageAlt: 'Haushaltsauflösung Wien – Möbel und Hausrat',
  },
  benefits: [
    { icon: <Tag size={22} />, title: 'Wertanrechnung', desc: 'Gut erhaltene Gegenstände und Möbel werden direkt vom Gesamtpreis abgezogen.' },
    { icon: <Shield size={22} />, title: 'Fixpreisgarantie', desc: 'Verbindlicher Preis nach Besichtigung – keine versteckten Kosten.' },
    { icon: <Clock size={22} />, title: 'Schnelle Abwicklung', desc: 'Flexibel und kurzfristig verfügbar – oft innerhalb von 24 Stunden.' },
    { icon: <CheckCircle size={22} />, title: 'Besenreine Übergabe', desc: 'Wohnung nach Auflösung besenrein übergeben – immer inklusive.' },
  ],
  process: [
    { icon: <MessageCircle size={24} />, title: 'Anfrage & Termin', desc: 'Formular ausfüllen oder anrufen. Wir vereinbaren einen kostenlosen Besichtigungstermin.' },
    { icon: <Calendar size={24} />, title: 'Bewertung & Fixpreis', desc: 'Wir bewerten Wertgegenstände und erstellen einen verbindlichen Gesamtpreis inklusive Abzüge.' },
    { icon: <CheckCircle size={24} />, title: 'Auflösung & Übergabe', desc: 'Vollständige Haushaltsauflösung mit besenreiner Übergabe – alles aus einer Hand.' },
  ],
  pricing: [
    { label: '1–2 Zimmer Haushalt', price: 'ab €490', icon: <Home size={20} />, highlight: false },
    { label: '3–4 Zimmer Haushalt', price: 'ab €890', icon: <Building2 size={20} />, highlight: true },
    { label: 'Großer Haushalt / Haus', price: 'auf Anfrage', icon: <Archive size={20} />, highlight: false },
  ],
  faq: [
    { q: 'Was kostet eine Haushaltsauflösung in Wien?', a: 'Kleine Haushalte ab €490, größere ab €890. Was noch brauchbar ist, senkt den Preis direkt. Nach der kostenlosen Besichtigung erhalten Sie den Fixpreis – kein Spielraum nach oben.' },
    { q: 'Was ist Wertanrechnung – und was bringt sie mir?', a: 'Gut erhaltene Möbel, Elektrogeräte, Antiquitäten – die bewerten wir vor Ort und ziehen den Wert direkt vom Gesamtpreis ab. Das spart Ihnen bares Geld, ohne dass Sie selbst etwas organisieren müssen.' },
    { q: 'Was passiert mit nicht mehr brauchbaren Sachen?', a: 'Kommt weg. Fachgerecht, sortenrein getrennt – Holz, Metall, Elektroschrott, Sperrmüll. Alles nach österreichischen Vorschriften. Das ist im Preis enthalten.' },
    { q: 'Können Sachen gespendet werden?', a: 'Was noch brauchbar ist, wird bei der Wertanrechnung berücksichtigt und von Ihrem Preis abgezogen. Den Rest entsorgen wir fachgerecht.' },
    { q: 'Wie lange dauert eine Haushaltsauflösung?', a: '2-Zimmer-Haushalt: oft ein Tag. Größere Haushalte: 1–2 Tage. Nach der Besichtigung sagen wir Ihnen genau, was Sie erwarten können.' },
  ],
  relatedServices: [
    { title: 'Wohnungsräumung', slug: '/wohnungsraeumung-wien', icon: <Home size={18} /> },
    { title: 'Nachlassräumung', slug: '/nachlassraeumung-wien', icon: <CheckCircle size={18} /> },
    { title: 'Messie-Wohnung', slug: '/messie-wohnung-wien', icon: <Building2 size={18} /> },
    { title: 'Kellerräumung', slug: '/kellerraeumung-wien', icon: <Archive size={18} /> },
  ],
};

export default function HaushaltsaufloesungPage() {
  return <ServicePageLayout data={data} />;
}
